import React, { useEffect } from "react";
import styled from "styled-components";
import {
  useTable,
  useRowSelect,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
} from "react-table";
import {
  Button,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { Link } from "react-router-dom";
import { Create } from "@material-ui/icons";
import apiHelper from "../../../../util/ApiHelper";
import { useLoading } from "../../../../util/LoadingProvider";
import { DatePicker } from "@material-ui/pickers";
import { CustomCheckbox } from "../../../../util";

const FilterDiv = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0 20px;

  p {
    font-size: 13px;
    line-height: 15px;
    color: #306f9d;
    padding-left: 3px;
    padding-right: 3px;
    cursor: pointer;

    span {
      color: #333333;
    }

    &.active {
      font-weight: bold;
      color: #333333;
    }

    &:first-child {
      padding-left: 0;
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  input: {
    width: "100%",
  },
  margin: {
    marginBottom: "40px",
  },
  flexRow: {
    display: "flex",
    justifyContent: "space-between",
    "& .MuiSelect-root": {
      paddingTop: "9px",
      paddingBottom: "9px",
    },
    "& input": {
      padding: "11px 14px",
    },
    "& label": {
      transform: "translate(14px, 12px) scale(1)",
    },
  },
  pagination: {
    "& .MuiTextField-root": {
      width: "35px",
      minWidth: "auto",
    },
    "& .MuiSelect-root": {
      paddingTop: "3px",
      paddingBottom: "3px",
    },
    "& input": {
      padding: "4px 0",
      marginLeft: "10px",
      marginRight: "10px",
    },
    "& button": {
      background: "white",
      color: "#cccccc",
      border: "1px solid #CCCCCC",
      minWidth: "auto",
      width: "24px",
      height: "24px",
      margin: "4px 0",
      "&.left": {
        borderRadius: "4px 0 0 4px",
        marginLeft: "10px",
      },
      "&.right": {
        borderRadius: " 0 4px 4px 0",
      },
    },
  },
}));

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <>
        <CustomCheckbox color="primary" ref={resolvedRef} {...rest} />
      </>
    );
  }
);

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <>
      <TextField
        id="search-field"
        label="Search Page"
        variant="outlined"
        value={value || ""}
        onChange={(e) => {
          setValue(e.target.value);
        }}
      />
      <Button
        type="submit"
        variant="outlined"
        color="primary"
        onClick={() => {
          onChange(value);
        }}
      >
        Search
      </Button>
    </>
  );
}

export default function PagesListingPage({disabled, ...props}) {
  const classes = useStyles();
  const [data, setData] = React.useState([]);
  const [allData, setAllData] = React.useState([]);
  const [filterPublished, setFilterPublished] = React.useState([]);
  const [filterDraft, setFilterDraft] = React.useState([]);
  const [filterStatus, setFilterStatus] = React.useState("all");
  const [selectedDate, handleDateChange] = React.useState(null);
  const { setLoading } = useLoading();

  useEffect(async () => {
    try {
      const response = await apiHelper.hknt.hkntGetPages({ category: "general" });
      setData(response.data.content);
      setAllData(response.data.content);
      setFilterPublished(
        response.data.content.filter((x) => x.status === "PUBLISHED")
      );
      setFilterDraft(response.data.content.filter((x) => x.status === "DRAFT"));
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: "Title",
        accessor: "title",
      },
      {
        Header: "Author",
        accessor: "updateBy",
      },
      {
        Header: "Status",
        accessor: "status",
      },
      {
        Header: "Last Update",
        accessor: "updateDate",
      },
      {
        Header: "Action",
        accessor: "",
      },
    ],
    [data]
  );

  const {
    getTableProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
    },
    useGlobalFilter,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: "selection",
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div>
              <IndeterminateCheckbox {...{...getToggleAllRowsSelectedProps(), disabled}} />
            </div>
          ),
        },
        ...columns,
      ]);
    }
  );

  const filterByDate = () => {
    const date = new Date(selectedDate);
    let month = date.getMonth() + 1;
    month = month < 10 ? "0" + month : month;
    const date_value = date.getFullYear() + "-" + month + "-01";

    apiHelper.hknt
      .hkntGetPages({ category: "general", date: date_value })
      .then((response) => {
        setData(response.data.content);
        setAllData(response.data.content);
        setFilterPublished(
          response.data.content.filter((x) => x.status === "PUBLISHED")
        );
        setFilterDraft(
          response.data.content.filter((x) => x.status === "DRAFT")
        );
      });
  };

  const filterDataWithStatus = (status) => {
    setFilterStatus(status);

    switch (status) {
      case "published":
        setData(filterPublished);
        break;

      case "draft":
        setData(filterDraft);
        break;

      case "all":
      default:
        setData(allData);
        break;
    }
  };

  return (
    data && (
      <>
        <Typography variant="h4" gutterBottom>
          Pages
        </Typography>
        <FilterDiv style={{ marginTop: "20px" }}>
          <p
            className={filterStatus === "all" && "active"}
            onClick={() => filterDataWithStatus("all")}
          >
            All <span>({allData.length})</span>
          </p>
          |
          <p
            className={filterStatus === "published" && "active"}
            onClick={() => filterDataWithStatus("published")}
          >
            Published <span>({filterPublished.length})</span>
          </p>
          |
          <p
            className={filterStatus === "draft" && "active"}
            onClick={() => filterDataWithStatus("draft")}
          >
            {" "}
            Drafts <span>({filterDraft.length})</span>
          </p>
        </FilterDiv>
        <div className={classes.flexRow}>
          <div>
            <DatePicker
              variant="inline"
              inputVariant="outlined"
              openTo="year"
              views={["year", "month"]}
              label="Year and Month"
              value={selectedDate}
              onChange={handleDateChange}
            />
            <Button
              type="submit"
              variant="outlined"
              color="primary"
              onClick={filterByDate}
            >
              Filter
            </Button>
          </div>
          <div>
            <GlobalFilter
              preGlobalFilteredRows={preGlobalFilteredRows}
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
            />
          </div>
        </div>

        <Paper>
          <Table aria-label="simple table" component="ul" {...getTableProps()}>
            <TableHead component="div">
              {headerGroups.map((headerGroup) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <TableCell
                      {...column.getHeaderProps()}
                      style={{ paddingTop: 6, paddingBottom: 6 }}
                    >
                      {column.render("Header")}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody component="div">
              {page.map((row, index) => {
                prepareRow(row);
                const generalPage = row.original;
                return (
                  <TableRow>
                    <TableCell style={{ paddingTop: 6, paddingBottom: 6 }}>
                      <IndeterminateCheckbox
                        {...{...row.getToggleRowSelectedProps(), disabled}}
                      />
                    </TableCell>
                    <TableCell
                      style={{ paddingTop: 6, paddingBottom: 6 }}
                      component="th"
                      scope="row"
                    >
                      <Link
                        to={`/hknt/pages/${generalPage.id}`}
                        style={{ color: "#306F9D", textDecoration: "none" }}
                      >
                        {generalPage.title}
                      </Link>
                    </TableCell>
                    <TableCell style={{ width: 160, color: "#306F9D", paddingTop: 6, paddingBottom: 6 }}>
                      {generalPage.updateBy}
                    </TableCell>
                    <TableCell style={{ width: 160, color: "#306F9D", paddingTop: 6, paddingBottom: 6 }}>
                      {generalPage.status.charAt(0).toUpperCase() +
                        generalPage.status.slice(1).toLowerCase()}
                    </TableCell>
                    <TableCell style={{ width: 200, paddingTop: 6, paddingBottom: 6 }}>
                      {generalPage.updateDate}
                    </TableCell>
                    <TableCell style={{ width: 160, paddingTop: 6, paddingBottom: 6 }}>
                      <Link to={`/hknt/pages/${generalPage.id}`}>
                        <Create fontSize="small" style={{ color: "#CCCCCC" }} />
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>

        <div className={classes.flexRow}>
          <div></div>
          <div className={classes.pagination}>
            Show rows:{" "}
            <TextField
              id="page-rows"
              select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
              variant="outlined"
              style={{ minWidth: "70px" }}
            >
              {[5, 10, 15, 20].map((pageSize) => (
                <MenuItem key={pageSize} value={pageSize}>
                  {pageSize}
                </MenuItem>
              ))}
            </TextField>
            Go to{" "}
            <TextField
              id="pagination-field"
              variant="outlined"
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                gotoPage(page);
              }}
            />
            {pageIndex * pageSize + 1} to{" "}
            {(pageIndex + 1) * pageSize > data.length
              ? data.length
              : (pageIndex + 1) * pageSize}{" "}
            of {data.length}
            <Button
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              className="left"
            >
              {"<"}
            </Button>
            <Button
              onClick={() => nextPage()}
              disabled={!canNextPage}
              className="right"
            >
              {">"}
            </Button>
          </div>
        </div>
      </>
    )
  );
}
